import { ApiError } from '@/api/client'
import type { SalesReport } from '@/api/goods'
import type { PassSales, ServicePayments, Sold } from '@/api/overview'
import type { Caller } from './abilities'
import type { PeriodKind } from './periods'
import { atLeast } from './permissions'

/// «Огляд» — the owner's first numbers, pure. SPEC: specs/reports.md §8–§14.
///
/// Every figure comes from the service that owns it; this file only decides
/// who sees the page, how the streams add up, and what a figure that did not
/// arrive is (R13: «—» with the reason, never 0).

// ---- who sees it ----

/// `reports ≥ view` or `finance ≥ view` — every route behind the page asks
/// the same (R6) — at a reach wider than `own`: a trainer's own sessions are
/// not the club's figures (§8.4). A god sees everything.
export const canSeeOverview = (caller: Caller): boolean =>
  caller.god ||
  (caller.profile.scope !== 'own' &&
    (atLeast(caller.profile.permissions.reports, 'view') || atLeast(caller.profile.permissions.finance, 'view')))

// ---- the period ----

/// The page's presets, in the order of the pills (§9).
export const OVERVIEW_PERIODS = ['thisMonth', 'lastMonth', 'month', 'year'] as const satisfies readonly PeriodKind[]
export type OverviewPeriodKind = (typeof OVERVIEW_PERIODS)[number]
export const DEFAULT_PERIOD: OverviewPeriodKind = 'thisMonth'

export const isOverviewPeriod = (value: unknown): value is OverviewPeriodKind =>
  typeof value === 'string' && (OVERVIEW_PERIODS as readonly string[]).includes(value)

// ---- a figure, or why there is none ----

/// Why a figure is «—» (R13): the server refused it to this role, or it could
/// not be read — an error, or a service not deployed yet.
export type Gap = 'forbidden' | 'failed'

export const gapOf = (error: unknown): Gap => (error instanceof ApiError && error.status === 403 ? 'forbidden' : 'failed')

/// A figure as the page holds it: its value, or the gap in its place.
export type Read<T> = Readonly<{ kind: 'value'; value: T }> | Readonly<{ kind: 'gap'; gap: Gap }>

/// From a query: its data — the last good answer included, while the next one
/// is on its way — else the reason it has none.
export const readOf = <T>(query: Readonly<{ data: T | undefined; error: unknown }>): Read<T> =>
  query.data !== undefined ? { kind: 'value', value: query.data } : { kind: 'gap', gap: gapOf(query.error) }

// ---- money ----

export const STREAMS = ['passes', 'services', 'goods'] as const
export type StreamKey = (typeof STREAMS)[number]

/// One stream's money as the page shows it (§8.2).
export type StreamFigures = Readonly<{
  sales: number
  revenueMinor: number
  refunds: number
  refundedMinor: number
  /// `revenueMinor − refundedMinor`: what the page calls «Виручка».
  netMinor: number
  averageMinor: number | null
}>

/// The average sale (§8.3, R9): the net over the sales, to the kopeck — null
/// with no sales, «—», never 0.
export const averageOf = (netMinor: number, sales: number): number | null =>
  sales > 0 ? Math.round(netMinor / sales) : null

const withRefunds = (report: Sold & Readonly<{ refunds: Readonly<{ count: number; amountMinor: number }> }>): StreamFigures => {
  const netMinor = report.revenueMinor - report.refunds.amountMinor
  return {
    sales: report.sales,
    revenueMinor: report.revenueMinor,
    refunds: report.refunds.count,
    refundedMinor: report.refunds.amountMinor,
    netMinor,
    averageMinor: averageOf(netMinor, report.sales),
  }
}

export const passFigures = (report: PassSales): StreamFigures => withRefunds(report)

export const serviceFigures = (report: ServicePayments): StreamFigures => withRefunds(report)

/// Goods have no returns yet (specs/shop.md §7): the revenue is the net.
export const goodsFigures = (report: Pick<SalesReport, 'totals'>): StreamFigures => ({
  sales: report.totals.sales,
  revenueMinor: report.totals.revenueMinor,
  refunds: 0,
  refundedMinor: 0,
  netMinor: report.totals.revenueMinor,
  averageMinor: averageOf(report.totals.revenueMinor, report.totals.sales),
})

export type Streams = Readonly<Record<StreamKey, Read<StreamFigures>>>

/// The whole club's money: the sum of the streams the page could read, and
/// the ones it could not — a total that leaves a stream out says so (R13).
export type Total = Readonly<{
  sales: number
  netMinor: number
  refundedMinor: number
  averageMinor: number | null
  missing: readonly StreamKey[]
}>

export const totalOf = (streams: Streams): Total => {
  const known = STREAMS.flatMap((key) => {
    const stream = streams[key]
    return stream.kind === 'value' ? [stream.value] : []
  })
  const sum = (key: 'sales' | 'netMinor' | 'refundedMinor') => known.reduce((total, row) => total + row[key], 0)
  const sales = sum('sales')
  const netMinor = sum('netMinor')
  return {
    sales,
    netMinor,
    refundedMinor: sum('refundedMinor'),
    averageMinor: averageOf(netMinor, sales),
    missing: STREAMS.filter((key) => streams[key].kind === 'gap'),
  }
}

/// With one club chosen: what was sold under no club — the network's passes,
/// the sessions with no club (§11). Null when nothing was, or no club is chosen.
export const unassignedOf = (...parts: readonly (Sold | null | undefined)[]): Sold | null => {
  const sold = parts.filter((part): part is Sold => part != null)
  const sales = sold.reduce((total, part) => total + part.sales, 0)
  return sales > 0 ? { sales, revenueMinor: sold.reduce((total, part) => total + part.revenueMinor, 0) } : null
}

/// A company on its first day: no client, no visitor, no sale — the page says
/// where the figures will come from instead of a wall of zeros.
export const isBlank = (figures: Readonly<{ base: number | null; visitors: number | null; total: Total }>): boolean =>
  (figures.base ?? 0) === 0 && (figures.visitors ?? 0) === 0 && figures.total.sales === 0 && figures.total.missing.length === 0

/// The best sellers the page lists — the plans already come best first.
export const TOP_PLANS = 5
